import React,{useState,useEffect,useCallback} from 'react';
import {settingsAPI,branchAPI,notificationAPI} from '../utils/api';
import {PageHeader,FormInput,Badge,DataTable} from '../components/common';
import {useLang} from '../i18n';
import {useAuth} from '../context/AuthContext';
import {
  Save,Send,Bell,AlertTriangle,BarChart3,Package,MessageSquare,Settings as SettingsIcon,
  CheckCircle,XCircle,Edit2,Check,X,Trash2,RefreshCw,Bot,Zap,Clock,Eye,
  ChevronDown,ChevronUp,Lock,Building2
} from 'lucide-react';
import toast from 'react-hot-toast';

// ── Notification event definitions (translated at render time) ────
const EVENT_KEYS=[
  {icon:'💰',key:'cash_register_closed',triggerType:'automatic',color:'var(--success)'},
  {icon:'🧾',key:'invoice_created',triggerType:'automatic',color:'var(--info)'},
  {icon:'📋',key:'sale_order',triggerType:'automatic',color:'var(--primary)'},
  {icon:'💵',key:'payment_received',triggerType:'automatic',color:'var(--accent)'},
  {icon:'📊',key:'daily_report',triggerType:'scheduled',color:'var(--warning)'},
  {icon:'📦',key:'low_stock',triggerType:'scheduled',color:'var(--danger)'},
  {icon:'🔴',key:'large_discount',triggerType:'automatic',color:'var(--danger)'},
];

const NOTIF_COLORS={cash_register_closed:'success',invoice_created:'info',sale_order:'primary',payment_received:'accent',daily_report:'warning',low_stock:'danger',large_discount:'danger',test:'primary'};

/* ── Branch Telegram Row ─────────────────────────────────────── */
function BranchRow({branch,onSaved,onTest,testing,canEdit,t}){
  const[editing,setEditing]=useState(false);
  const[val,setVal]=useState(branch.telegram_chat_id||'');
  const[saving,setSaving]=useState(false);
  const ok=!!(branch.telegram_chat_id);

  const save=async()=>{
    setSaving(true);
    try{
      await notificationAPI.updateBranchTelegram(branch.id,{telegram_chat_id:val.trim()||null});
      toast.success(`${branch.name} ${t('common.updated')}`);onSaved();setEditing(false);
    }catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))}
    finally{setSaving(false)};
  };

  return(
    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'13px 16px',
      borderRadius:10,background:'var(--s2)',
      border:`1px solid ${ok?'rgba(34,197,94,.25)':'rgba(239,68,68,.15)'}`,gap:12,flexWrap:'wrap'}}>
      {/* Branch info */}
      <div style={{display:'flex',alignItems:'center',gap:12,minWidth:0,flex:1}}>
        <div style={{width:36,height:36,borderRadius:8,background:ok?'var(--sm)':'var(--dm)',
          display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
          {ok?<CheckCircle size={17} color="var(--success)"/>:<XCircle size={17} color="var(--danger)"/>}
        </div>
        <div style={{minWidth:0,flex:1}}>
          <div style={{display:'flex',alignItems:'center',gap:7,flexWrap:'wrap',marginBottom:3}}>
            <span style={{fontWeight:700,fontSize:13}}>{branch.name}</span>
            <span style={{fontSize:10,fontWeight:700,color:'var(--primary)',background:'var(--pm)',padding:'1px 6px',borderRadius:4}}>{branch.code}</span>
            {branch.is_headquarters&&<span style={{fontSize:10,fontWeight:700,color:'var(--accent)',background:'var(--am)',padding:'1px 6px',borderRadius:4}}>HQ</span>}
            <span style={{fontSize:10,fontWeight:600,color:ok?'var(--success)':'var(--danger)'}}>
              {ok?t('settings.connected'):t('settings.notConfigured')}
            </span>
          </div>
          {/* Chat ID display or input */}
          {editing?(
            <div style={{display:'flex',alignItems:'center',gap:7,flexWrap:'wrap',marginTop:4}}>
              <input value={val} onChange={e=>setVal(e.target.value)}
                placeholder="-100123456789 or @channelname"
                onKeyDown={e=>e.key==='Enter'?save():e.key==='Escape'&&(setEditing(false),setVal(branch.telegram_chat_id||''))}
                autoFocus
                style={{padding:'7px 11px',borderRadius:7,border:'1px solid var(--primary)',background:'var(--s3)',
                  color:'var(--text)',fontSize:12,width:210,fontFamily:'monospace'}}/>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
                {saving?<RefreshCw size={12} style={{animation:'spin 1s linear infinite'}}/>:<Check size={12}/>} {t('settings.saveChatId')}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={()=>{setEditing(false);setVal(branch.telegram_chat_id||'')}}>
                <X size={12}/>
              </button>
            </div>
          ):(
            <div style={{fontSize:11,color:'var(--td)',marginTop:2,fontFamily:ok?'monospace':'inherit',display:'flex',alignItems:'center',gap:6}}>
              {ok?(
                <><span style={{color:'var(--success)',fontWeight:600}}>{branch.telegram_chat_id}</span></>
              ):(
                <span>{t('settings.noConfigDesc')}</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      {!editing&&(
        <div style={{display:'flex',alignItems:'center',gap:7,flexShrink:0}}>
          {ok&&<button className="btn btn-ghost btn-sm" style={{color:'var(--info)',fontSize:12}}
            onClick={()=>onTest(branch)} disabled={testing===String(branch.id)}>
            {testing===String(branch.id)
              ?<><RefreshCw size={11} style={{animation:'spin 1s linear infinite'}}/> {t('settings.sending')}</>
              :<><Send size={11}/> {t('settings.testSend')}</>}
          </button>}
          {canEdit&&<button className="btn btn-ghost btn-sm" style={{fontSize:12}} onClick={()=>setEditing(true)}>
            <Edit2 size={11}/> {ok?t('settings.editChatId'):t('settings.setChatId')}
          </button>}
          {ok&&canEdit&&<button className="btn btn-ghost btn-sm" style={{color:'var(--danger)'}}
            onClick={async()=>{
              await notificationAPI.updateBranchTelegram(branch.id,{telegram_chat_id:null});
              toast.success(t('common.deleted'));onSaved();
            }}><X size={12}/></button>}
        </div>
      )}
    </div>
  );
}

/* ═══ MAIN PAGE ═══════════════════════════════════════════════ */
export default function SettingsPage(){
  const{t}=useLang();
  const{isOwner,myBranchId,hasPermission}=useAuth();
  const canEdit=isOwner||hasPermission('settings.edit');

  const[settings,setSettings]=useState([]);
  const[branches,setBranches]=useState([]);
  const[ld,setLd]=useState(true);
  const[edited,setEdited]=useState({});
  const[tab,setTab]=useState('general');
  const[notifications,setNotifications]=useState([]);
  const[notifLd,setNotifLd]=useState(false);
  const[botInfo,setBotInfo]=useState(null);
  const[botLd,setBotLd]=useState(false);
  const[testBranch,setTestBranch]=useState('');
  const[testMsg,setTestMsg]=useState('');
  const[sending,setSending]=useState('');
  const[testing,setTesting]=useState('');
  const[showEvents,setShowEvents]=useState(true);
  const[notifFilter,setNotifFilter]=useState('');

  // Translated events
  const EVENTS=EVENT_KEYS.map(e=>({
    ...e,
    title: t(`settings.${e.key==='cash_register_closed'?'cashRegisterClosed':e.key==='invoice_created'?'invoiceCreated':e.key==='sale_order'?'saleOrder':e.key==='payment_received'?'paymentReceived':e.key==='daily_report'?'dailyReport':e.key==='low_stock'?'lowStock':e.key==='large_discount'?'largeDiscount':e.key}`),
    desc: t(`settings.${e.key==='cash_register_closed'?'cashRegisterClosedDesc':e.key==='invoice_created'?'invoiceCreatedDesc':e.key==='sale_order'?'saleOrderDesc':e.key==='payment_received'?'paymentReceivedDesc':e.key==='daily_report'?'dailyReportDesc':e.key==='low_stock'?'lowStockDesc':e.key==='large_discount'?'largeDiscountDesc':e.key}`),
    trigger: e.triggerType==='automatic'?t('settings.automatic'):t('settings.scheduled'),
  }));

  const loadData=useCallback(async()=>{
    try{
      let allBranches=[];
      if(isOwner){
        const[s,b]=await Promise.all([settingsAPI.getAll(),branchAPI.getAll({limit:100})]);
        setSettings(Array.isArray(s.data)?s.data:s.data.data||[]);
        allBranches=b.data.data||[];
      } else {
        // Non-owner: only load their branch
        const b=await branchAPI.getAll({limit:100});
        const all=b.data.data||[];
        allBranches=myBranchId?all.filter(br=>br.id===myBranchId):[];
      }
      setBranches(allBranches);
    }catch{toast.error(t('common.failedToLoad'))}
    finally{setLd(false)};
  },[isOwner,myBranchId]);

  useEffect(()=>{loadData()},[loadData]);

  const loadBotStatus=useCallback(async()=>{
    setBotLd(true);
    try{const r=await notificationAPI.botStatus();setBotInfo(r.data)}
    catch{setBotInfo({configured:false})}finally{setBotLd(false)};
  },[]);
  useEffect(()=>{if(tab==='telegram')loadBotStatus()},[tab,loadBotStatus]);

  const loadNotifications=useCallback(async()=>{
    setNotifLd(true);
    try{
      const p={limit:100};
      if(notifFilter) p.type=notifFilter;
      // Non-owner: filter to their branch
      if(!isOwner&&myBranchId) p.branch_id=myBranchId;
      const r=await notificationAPI.getAll(p);
      setNotifications(r.data.data||r.data||[]);
    }catch{toast.error(t('common.failedToLoad'))}
    finally{setNotifLd(false)};
  },[notifFilter,isOwner,myBranchId]);
  useEffect(()=>{if(tab==='notifications')loadNotifications()},[tab,loadNotifications]);

  const groups=[...new Set(settings.map(s=>s.group))].sort();
  const getVal=s=>edited[s.key]!==undefined?edited[s.key]:s.value;
  const updateVal=(key,val)=>setEdited(e=>({...e,[key]:val}));
  const save=async()=>{
    try{
      const list=Object.entries(edited).map(([key,value])=>{
        const orig=settings.find(s=>s.key===key);
        return{key,value:String(value),type:orig?.type||'string',group:orig?.group||'general'};
      });
      await settingsAPI.bulkUpdate({settings:list});
      toast.success(t('settings.settingsSaved'));setEdited({});
      const r=await settingsAPI.getAll();setSettings(Array.isArray(r.data)?r.data:r.data.data||[]);
    }catch{toast.error(t('common.failedToSave'))};
  };

  const testBranchTelegram=async(branch)=>{
    setTesting(String(branch.id));
    try{
      await notificationAPI.testTelegram({branch_id:branch.id,message:`🔔 ${t('settings.testSent')}\n\n${t('nav.branches')}: ${branch.name} ✅`});
      toast.success(`${t('settings.testSent')} → ${branch.name}!`);
    }catch(e){toast.error(e.response?.data?.error||t('settings.sendFailed'))}
    finally{setTesting('')};
  };

  const sendTest=async()=>{
    if(!testBranch)return toast.error(t('settings.selectBranch'));
    setSending('test');
    try{
      await notificationAPI.testTelegram({branch_id:parseInt(testBranch),message:testMsg||`🔔 ${t('settings.testSent')}`});
      toast.success(t('settings.testSent'));setTestMsg('');
    }catch(e){toast.error(e.response?.data?.error||t('settings.sendFailed'))}
    finally{setSending('')};
  };

  const sendDaily=async()=>{
    setSending('daily');
    try{await notificationAPI.sendDailyReport();toast.success(t('settings.dailySent'));}
    catch(e){toast.error(e.response?.data?.error||t('settings.sendFailed'))}
    finally{setSending('')};
  };

  const sendLowStock=async()=>{
    setSending('lowstock');
    try{await notificationAPI.sendLowStockAlert();toast.success(t('settings.lowStockSent'));}
    catch(e){toast.error(e.response?.data?.error||t('settings.sendFailed'))}
    finally{setSending('')};
  };

  const markRead=async id=>{try{await notificationAPI.markRead(id);loadNotifications()}catch{}};
  const deleteNotif=async id=>{try{await notificationAPI.deleteOne(id);loadNotifications()}catch{}};

  const configuredCount=branches.filter(b=>b.telegram_chat_id).length;

  // Branch options for test send — non-owners only see their branch
  const branchOpts=[
    {value:'',label:t('settings.selectBranch')},
    ...branches.map(b=>({value:String(b.id),label:`${b.name} ${b.telegram_chat_id?'✅':'❌'}`}))
  ];

  const unreadCount=notifications.filter(n=>!n.is_read).length;

  const tabs=[
    {id:'general',label:t('settings.general'),icon:SettingsIcon},
    {id:'telegram',label:t('settings.telegramAlerts'),icon:MessageSquare,badge:configuredCount>0?`${configuredCount}/${branches.length}`:null,bc:'success'},
    {id:'notifications',label:t('settings.notificationLog'),icon:Bell,badge:unreadCount>0?unreadCount:null,bc:'danger'},
  ];

  if(ld)return<div style={{padding:60,textAlign:'center',color:'var(--td)'}}>{t('common.loading')}</div>;

  return(
    <div>
      <PageHeader
        title={t('settings.title')}
        subtitle={t('settings.subtitle')}
        action={isOwner&&Object.keys(edited).length>0&&(
          <button className="btn btn-primary" onClick={save}>
            <Save size={16}/> {t('settings.save')} ({Object.keys(edited).length})
          </button>
        )}
      />

      {/* Tabs */}
      <div style={{display:'flex',gap:2,marginBottom:20,borderBottom:'1px solid var(--border)'}}>
        {tabs.map(tab_=>( 
          <button key={tab_.id} onClick={()=>setTab(tab_.id)}
            style={{display:'flex',alignItems:'center',gap:6,padding:'10px 16px',background:'none',border:'none',cursor:'pointer',
              fontWeight:tab===tab_.id?700:400,color:tab===tab_.id?'var(--primary)':'var(--td)',
              borderBottom:tab===tab_.id?'2px solid var(--primary)':'2px solid transparent',fontSize:13,transition:'all .15s'}}>
            <tab_.icon size={14}/> {tab_.label}
            {tab_.badge!=null&&<span style={{background:`var(--${tab_.bc})`,color:'#fff',fontSize:10,fontWeight:700,padding:'1px 6px',borderRadius:20,marginLeft:2}}>{tab_.badge}</span>}
          </button>
        ))}
        <div style={{flex:1}}/>
        <button className="btn btn-ghost btn-sm" onClick={loadData} style={{marginBottom:4,alignSelf:'center'}}>
          <RefreshCw size={12}/>
        </button>
      </div>

      {/* ══ GENERAL TAB ══ */}
      {tab==='general'&&(
        isOwner?(
          <>
            {/* ── Bank / Invoice Details ── */}
            <div className="card" style={{marginBottom:16}}>
              <h4 style={{fontSize:14,fontWeight:700,marginBottom:14,color:'var(--primary)',display:'flex',alignItems:'center',gap:8}}>
                🏦 Bank &amp; Invoice Details
              </h4>
              <div className="form-grid">
                {[
                  ['company_name','Company Name (EN)','text'],
                  ['company_name_kh','ឈ្មោះក្រុមហ៊ុន (KH)','text'],
                  ['company_address','Address / អាសយដ្ឋាន','text'],
                  ['company_phone','Phone / ទូរស័ព្ទ','text'],
                  ['company_email','Email / អ៊ីម៉ែល','text'],
                  ['tax_id','VAT TIN / លេខអត្តសញ្ញាណពន្ធ','text'],
                  ['bank_name','Bank Name / ឈ្មោះធនាគារ','text'],
                  ['bank_address','Bank Address / អាសយដ្ឋានធនាគារ','text'],
                  ['account_name','Account Name / ឈ្មោះគណនី','text'],
                  ['account_number','Account Number / លេខគណនី','text'],
                ].map(([key,label])=>{
                  const cur=settings.find(s=>s.key===key);
                  const curVal=cur?getVal(cur):(edited[key]??'');
                  return(
                    <FormInput key={key} label={label}
                      value={edited[key]!==undefined?edited[key]:curVal}
                      onChange={v=>{
                        setEdited(e=>({...e,[key]:v}));
                        if(!cur) setSettings(s=>[...s,{key,value:v,type:'string',group:'invoice'}]);
                      }}
                      type="text"/>
                  );
                })}
              </div>
            </div>
            {groups.map(g=>(
              <div key={g} className="card" style={{marginBottom:16}}>
                <h4 style={{fontSize:14,fontWeight:700,marginBottom:14,textTransform:'capitalize',color:'var(--primary)'}}>{g} {t('settings.generalSettings')}</h4>
                <div className="form-grid">
                  {settings.filter(s=>s.group===g).map(s=>(
                    <FormInput key={s.key} label={s.key.replace(/\./g,' → ').replace(/_/g,' ')} value={getVal(s)} onChange={v=>updateVal(s.key,v)}
                      type={s.type==='boolean'?undefined:s.type==='number'?'number':'text'}
                      options={s.type==='boolean'?[{value:'true',label:t('common.yes')},{value:'false',label:t('common.no')}]:undefined}/>
                  ))}
                </div>
              </div>
            ))}
            {groups.length===0&&<div style={{textAlign:'center',padding:40,color:'var(--td)'}}>{t('common.noData')}</div>}
          </>
        ):(
          <div style={{padding:'40px 20px',textAlign:'center'}}>
            <Lock size={36} color="var(--border)" style={{margin:'0 auto 12px'}}/>
            <div style={{fontWeight:600,marginBottom:6}}>{t('common.accessDenied')}</div>
            <div style={{color:'var(--td)',fontSize:13}}>{t('common.contactAdmin')}</div>
          </div>
        )
      )}

      {/* ══ TELEGRAM TAB ══ */}
      {tab==='telegram'&&(
        <>
          {/* Bot status banner */}
          <div style={{padding:'13px 16px',borderRadius:12,marginBottom:16,
            background:botInfo?.ok?'var(--sm)':botInfo?.configured?'var(--wm)':'var(--dm)',
            border:`1px solid ${botInfo?.ok?'rgba(34,197,94,.3)':botInfo?.configured?'rgba(245,158,11,.3)':'rgba(239,68,68,.3)'}`,
            display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
            <div style={{display:'flex',alignItems:'center',gap:12}}>
              <div style={{width:38,height:38,borderRadius:9,display:'flex',alignItems:'center',justifyContent:'center',
                border:`2px solid ${botInfo?.ok?'var(--success)':botInfo?.configured?'var(--warning)':'var(--danger)'}`,background:'transparent'}}>
                <Bot size={19} color={botInfo?.ok?'var(--success)':botInfo?.configured?'var(--warning)':'var(--danger)'}/>
              </div>
              <div>
                {botLd?(
                  <div style={{color:'var(--td)',fontSize:13}}>{t('settings.botCheckStatus')}</div>
                ):botInfo?.ok?(
                  <>
                    <div style={{fontWeight:700,fontSize:13,color:'var(--success)',display:'flex',alignItems:'center',gap:5}}>
                      <CheckCircle size={13}/> {t('settings.botConnected')} — @{botInfo.bot?.username}
                    </div>
                    <div style={{fontSize:11,color:'var(--td)',marginTop:2}}>{botInfo.bot?.first_name} · {t('settings.botApiReady')}</div>
                  </>
                ):botInfo?.configured?(
                  <>
                    <div style={{fontWeight:700,fontSize:13,color:'var(--warning)',display:'flex',alignItems:'center',gap:5}}>
                      <AlertTriangle size={13}/> {t('settings.botInvalid')}
                    </div>
                    <div style={{fontSize:11,color:'var(--td)',marginTop:2}}>{t('settings.botInvalidDesc')}</div>
                  </>
                ):(
                  <>
                    <div style={{fontWeight:700,fontSize:13,color:'var(--danger)',display:'flex',alignItems:'center',gap:5}}>
                      <XCircle size={13}/> {t('settings.botNotConfigured')}
                    </div>
                    <div style={{fontSize:11,color:'var(--td)',marginTop:2}}>
                      {t('settings.botTokenDesc')}&nbsp;
                      <code style={{background:'var(--s3)',padding:'0 5px',borderRadius:4}}>TELEGRAM_BOT_TOKEN</code>
                    </div>
                  </>
                )}
              </div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={loadBotStatus} disabled={botLd}>
              <RefreshCw size={12} style={botLd?{animation:'spin 1s linear infinite'}:{}}/> {t('settings.refresh')}
            </button>
          </div>

          {/* Non-owner info banner */}
          {!isOwner&&(
            <div style={{display:'flex',alignItems:'center',gap:8,padding:'10px 14px',
              background:'rgba(0,212,170,0.06)',border:'1px solid rgba(0,212,170,0.2)',
              borderRadius:10,marginBottom:14,fontSize:12}}>
              <Lock size={13} color="var(--primary)"/>
              <span style={{color:'var(--primary)'}}>{t('settings.yourBranchOnly')}</span>
            </div>
          )}

          {/* Branch Telegram Config */}
          <div className="card" style={{marginBottom:16}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14,flexWrap:'wrap',gap:10}}>
              <div>
                <h4 style={{fontSize:14,fontWeight:700,margin:0,display:'flex',alignItems:'center',gap:8}}>
                  <MessageSquare size={14} color="var(--primary)"/>
                  {t('settings.branchTelegramConfig')}
                  <span style={{fontSize:11,color:'var(--td)',fontWeight:400}}>{configuredCount}/{branches.length} {t('settings.configured')}</span>
                </h4>
                <p style={{fontSize:11,color:'var(--td)',marginTop:4,marginBottom:0}}>
                  {t('settings.branchTelegramDesc')} ·&nbsp;
                  <code style={{background:'var(--s3)',padding:'0 4px',borderRadius:3}}>@userinfobot</code>
                </p>
              </div>
              {/* Progress bar (owner only) */}
              {isOwner&&branches.length>0&&(
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <div style={{width:80,height:5,borderRadius:3,background:'var(--s3)',overflow:'hidden'}}>
                    <div style={{height:'100%',borderRadius:3,background:'var(--success)',transition:'width .3s',
                      width:`${Math.round((configuredCount/branches.length)*100)}%`}}/>
                  </div>
                  <span style={{fontSize:11,color:'var(--td)',fontWeight:600}}>{Math.round((configuredCount/branches.length)*100)}%</span>
                </div>
              )}
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {branches.map(b=>(
                <BranchRow key={b.id} branch={b} onSaved={loadData} onTest={testBranchTelegram}
                  testing={testing} canEdit={canEdit} t={t}/>
              ))}
              {branches.length===0&&(
                <div style={{textAlign:'center',padding:'24px 0',color:'var(--td)',display:'flex',flexDirection:'column',alignItems:'center',gap:8}}>
                  <Building2 size={32} color="var(--border)"/>
                  {t('settings.noBranches')}
                </div>
              )}
            </div>
          </div>

          {/* Notification Events accordion */}
          <div className="card" style={{marginBottom:16,padding:0,overflow:'hidden'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'13px 16px',background:'var(--s2)',cursor:'pointer'}}
              onClick={()=>setShowEvents(v=>!v)}>
              <h4 style={{margin:0,fontSize:14,fontWeight:700,display:'flex',alignItems:'center',gap:8}}>
                <Bell size={14} color="var(--primary)"/>
                {t('settings.events')}
                <span style={{fontSize:11,color:'var(--td)',fontWeight:400}}>{t('settings.eventsCount',{n:EVENTS.length})}</span>
              </h4>
              {showEvents?<ChevronUp size={15} color="var(--td)"/>:<ChevronDown size={15} color="var(--td)"/>}
            </div>
            {showEvents&&(
              <div style={{padding:14}}>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(230px,1fr))',gap:10}}>
                  {EVENTS.map(ev=>(
                    <div key={ev.key} style={{padding:13,borderRadius:9,background:'var(--s2)',border:'1px solid var(--border)',position:'relative',overflow:'hidden'}}>
                      <div style={{position:'absolute',top:0,left:0,width:3,height:'100%',background:ev.color}}/>
                      <div style={{paddingLeft:8}}>
                        <div style={{fontSize:18,marginBottom:5}}>{ev.icon}</div>
                        <div style={{fontWeight:700,fontSize:12,marginBottom:3}}>{ev.title}</div>
                        <div style={{fontSize:11,color:'var(--td)',marginBottom:9,lineHeight:1.5}}>{ev.desc}</div>
                        <Badge variant={ev.triggerType==='automatic'?'success':'warning'}>
                          {ev.triggerType==='automatic'?<><Zap size={9}/> {ev.trigger}</>:<><Clock size={9}/> {ev.trigger}</>}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Test message + Manual triggers */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
            {/* Test message */}
            <div className="card">
              <h4 style={{fontSize:14,fontWeight:700,marginBottom:4,display:'flex',alignItems:'center',gap:7}}>
                <Send size={13} color="var(--primary)"/>{t('settings.sendTestMessage')}
              </h4>
              <p style={{fontSize:11,color:'var(--td)',marginBottom:14}}>{t('settings.sendTestDesc')}</p>
              <FormInput label={t('nav.branches')} value={testBranch} onChange={setTestBranch} options={branchOpts}/>
              <FormInput label={t('settings.customMessage')} value={testMsg} onChange={setTestMsg} placeholder={t('settings.leaveBlankDefault')}/>
              <button className="btn btn-primary" onClick={sendTest} disabled={!!sending}
                style={{width:'100%',justifyContent:'center',marginTop:4}}>
                {sending==='test'
                  ?<><RefreshCw size={13} style={{animation:'spin 1s linear infinite'}}/> {t('settings.sending')}</>
                  :<><Send size={13}/> {t('settings.sendTestBtn')}</>}
              </button>
            </div>

            {/* Manual triggers (owner only) */}
            <div className="card">
              <h4 style={{fontSize:14,fontWeight:700,marginBottom:4,display:'flex',alignItems:'center',gap:7}}>
                <Zap size={13} color="var(--warning)"/>{t('settings.manualTriggers')}
              </h4>
              <p style={{fontSize:11,color:'var(--td)',marginBottom:14}}>{t('settings.manualTriggersDesc')}</p>
              {isOwner?(
                <>
                  <button className="btn btn-primary" onClick={sendDaily} disabled={!!sending}
                    style={{width:'100%',justifyContent:'center',marginBottom:10,padding:'11px 0'}}>
                    {sending==='daily'
                      ?<><RefreshCw size={13} style={{animation:'spin 1s linear infinite'}}/> {t('settings.sending')}</>
                      :<><BarChart3 size={13}/> 📊 {t('settings.dailySalesReport')}</>}
                  </button>
                  <button className="btn btn-danger" onClick={sendLowStock} disabled={!!sending}
                    style={{width:'100%',justifyContent:'center',padding:'11px 0'}}>
                    {sending==='lowstock'
                      ?<><RefreshCw size={13} style={{animation:'spin 1s linear infinite'}}/> {t('settings.sending')}</>
                      :<><Package size={13}/> 📦 {t('settings.lowStockAlert')}</>}
                  </button>
                </>
              ):(
                <div style={{display:'flex',alignItems:'center',gap:8,padding:'14px',background:'var(--s2)',borderRadius:8,fontSize:12,color:'var(--td)'}}>
                  <Lock size={14}/>{t('common.accessDenied')}
                </div>
              )}
              <div style={{marginTop:12,padding:'10px 12px',background:'var(--s2)',borderRadius:8,fontSize:11,color:'var(--td)',lineHeight:1.8}}>
                <div>
                  <Clock size={10} style={{verticalAlign:'middle',marginRight:4,color:'var(--warning)'}}/>
                  <strong>{t('settings.autoSchedule')}:</strong>{' '}
                  <code style={{background:'var(--s3)',padding:'0 4px',borderRadius:3}}>20:00</code>
                </div>
                <div>
                  <AlertTriangle size={10} style={{verticalAlign:'middle',marginRight:4,color:'var(--danger)'}}/>
                  <strong>{t('settings.discountThresholdLabel')}:</strong>{' '}
                  ≥ <code style={{background:'var(--s3)',padding:'0 4px',borderRadius:3}}>10%</code>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ══ NOTIFICATION LOG TAB ══ */}
      {tab==='notifications'&&(
        <>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12,flexWrap:'wrap',gap:8}}>
            <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
              {[{value:'',label:t('settings.all')},...EVENT_KEYS.map(e=>({
                value:e.key,
                label:`${e.icon} ${t(`settings.${e.key==='cash_register_closed'?'cashRegisterClosed':e.key==='invoice_created'?'invoiceCreated':e.key==='sale_order'?'saleOrder':e.key==='payment_received'?'paymentReceived':e.key==='daily_report'?'dailyReport':e.key==='low_stock'?'lowStock':'largeDiscount'}`)}`
              }))].map(opt=>(
                <button key={opt.value} className={`filter-btn ${notifFilter===opt.value?'active':''}`}
                  style={{fontSize:11}} onClick={()=>setNotifFilter(opt.value)}>{opt.label}</button>
              ))}
            </div>
            <button className="btn btn-ghost btn-sm" onClick={loadNotifications} disabled={notifLd}>
              <RefreshCw size={12} style={notifLd?{animation:'spin 1s linear infinite'}:{}}/> {t('settings.refresh')}
            </button>
          </div>

          {/* Stats */}
          {notifications.length>0&&(
            <div style={{display:'flex',gap:10,marginBottom:14,flexWrap:'wrap'}}>
              {[
                [t('settings.total'),notifications.length,'var(--primary)'],
                [t('settings.unreadLabel'),notifications.filter(n=>!n.is_read).length,'var(--warning)'],
                [t('settings.telegramChannel'),notifications.filter(n=>n.channel==='telegram').length,'var(--success)'],
                [t('settings.systemChannel'),notifications.filter(n=>n.channel==='system').length,'var(--td)'],
              ].map(([l,v,c])=>(
                <div key={l} style={{padding:'7px 14px',borderRadius:8,background:'var(--s2)',border:'1px solid var(--border)',textAlign:'center',minWidth:65}}>
                  <div style={{fontSize:17,fontWeight:800,color:c}}>{v}</div>
                  <div style={{fontSize:10,color:'var(--td)',fontWeight:600}}>{l}</div>
                </div>
              ))}
            </div>
          )}

          <DataTable loading={notifLd}
            columns={[
              {key:'time',label:t('common.time'),render:r=>r.sent_at?new Date(r.sent_at).toLocaleString('en-GB',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}):'—'},
              {key:'type',label:t('common.type'),render:r=>(
                <div style={{display:'flex',alignItems:'center',gap:5}}>
                  <span>{EVENT_KEYS.find(e=>e.key===r.type)?.icon||'🔔'}</span>
                  <Badge variant={NOTIF_COLORS[r.type]||'primary'} style={{fontSize:10}}>{(r.type||'').replace(/_/g,' ')}</Badge>
                </div>
              )},
              {key:'title',label:t('common.name'),render:r=><span style={{fontWeight:r.is_read?400:700}}>{r.title}</span>},
              {key:'branch',label:t('common.branch'),render:r=>r.metadata?.branch_name?
                <span style={{fontSize:11,background:'var(--pm)',color:'var(--primary)',padding:'2px 7px',borderRadius:5,fontWeight:600}}>{r.metadata.branch_name}</span>:'—'},
              {key:'channel',label:t('settings.telegram'),render:r=>(
                <Badge variant={r.channel==='telegram'?'accent':'primary'}>
                  {r.channel==='telegram'?`📱 ${t('settings.telegramChannel')}`:`🖥 ${t('settings.systemChannel')}`}
                </Badge>
              )},
              {key:'status',label:t('common.status'),render:r=>r.is_read?
                <Badge variant="success"><Eye size={10}/> {t('settings.read')}</Badge>:
                <Badge variant="warning">{t('settings.unreadLabel')}</Badge>},
            ]}
            data={notifications}
            actions={r=>(
              <div className="flex gap-2">
                {!r.is_read&&<button className="btn btn-ghost btn-sm" onClick={()=>markRead(r.id)} title={t('settings.markRead')}><Check size={12}/></button>}
                <button className="btn btn-ghost btn-sm" style={{color:'var(--danger)'}} onClick={()=>deleteNotif(r.id)} title={t('settings.deleteNotif')}><Trash2 size={12}/></button>
              </div>
            )}
          />
          {notifications.length===0&&!notifLd&&(
            <div style={{textAlign:'center',padding:40,color:'var(--td)'}}>{t('common.noData')}</div>
          )}
        </>
      )}
    </div>
  );
}
